import { Howl, Howler } from 'howler';

/**
 * Utility class for managing global Howler.js audio settings and operations
 */
export class HowlerAudioManager {
  private static instance: HowlerAudioManager;
  private initialized = false;
  private muted = false;
  private volume = 1.0;

  private constructor() {
    // Private constructor to enforce singleton pattern
  }

  /**
   * Get the singleton instance
   */
  public static getInstance(): HowlerAudioManager {
    if (!HowlerAudioManager.instance) {
      HowlerAudioManager.instance = new HowlerAudioManager();
    }
    return HowlerAudioManager.instance;
  }

  /**
   * Initialize the Howler global settings
   */
  public initialize(): void {
    if (this.initialized) return;

    // Set global defaults
    Howler.autoUnlock = true;
    Howler.html5PoolSize = 10;
    
    // Prefer Web Audio API over HTML5 Audio
    Howler.usingWebAudio = true;
    
    // Directly test audio support - useful for debugging
    const testAudio = new Audio();
    const audioSupport = {
      mp3: testAudio.canPlayType('audio/mpeg').replace(/^no$/, ''),
      wav: testAudio.canPlayType('audio/wav; codecs="1"').replace(/^no$/, ''),
      webm: testAudio.canPlayType('audio/webm; codecs="vorbis"').replace(/^no$/, '')
    };
    
    // Check codec support
    const codecSupport = {
      mp3: Howler.codecs('mp3'),
      wav: Howler.codecs('wav'),
      webm: Howler.codecs('webm'),
      ogg: Howler.codecs('ogg')
    };
    
    // Log full configuration for debugging
    console.info('🔊 HowlerAudioManager: Initialized with Web Audio API');
    console.info('🔊 HowlerAudioManager: Using Web Audio API:', Howler.usingWebAudio);
    console.info('🔊 HowlerAudioManager: HTML5 Audio Pool Size:', Howler.html5PoolSize);
    console.info('🔊 HowlerAudioManager: Codec support (Howler):', codecSupport);
    console.info('🔊 HowlerAudioManager: Audio support (browser):', audioSupport);
    console.info('🔊 HowlerAudioManager: User agent:', navigator.userAgent);
    
    this.initialized = true;
  }

  /**
   * Set global volume (0-1)
   */
  public setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    Howler.volume(this.volume);
    console.debug(`🔊 HowlerAudioManager: Volume set to ${this.volume}`);
  }

  /**
   * Get current volume
   */
  public getVolume(): number {
    return this.volume;
  }

  /**
   * Mute/unmute all sounds
   */
  public setMuted(muted: boolean): void {
    this.muted = muted;
    Howler.mute(this.muted);
    console.debug(`🔊 HowlerAudioManager: ${muted ? 'Muted' : 'Unmuted'} audio`);
  }

  /**
   * Check if audio is muted
   */
  public isMuted(): boolean {
    return this.muted;
  }

  /**
   * Create a Howl instance for a blob URL
   */
  public createFromBlob(
    blob: Blob, 
    format: string = 'webm',
    options: Partial<{
      volume: number,
      loop: boolean,
      onend: () => void,
      onerror: (soundId: number, error: unknown) => void
    }> = {}
  ): { howl: Howl, url: string } {
    const url = URL.createObjectURL(blob);
    
    const howl = new Howl({
      src: [url],
      format: [format],
      html5: true,
      volume: options.volume ?? 1.0,
      loop: options.loop ?? false,
      onend: options.onend,
      onloaderror: (soundId, error) => {
        if (options.onerror) options.onerror(soundId, error);
      },
      onplayerror: (soundId, error) => {
        if (options.onerror) options.onerror(soundId, error);
      }
    });
    
    return { howl, url };
  }
  
  /**
   * Clean up a Howl instance created from a blob
   */
  public cleanupBlobHowl(howl: Howl, url: string): void {
    if (howl) {
      howl.stop();
      howl.unload();
    }
    URL.revokeObjectURL(url);
  }
  
  /**
   * Check if the Web Audio API is available in this browser
   */
  public isWebAudioSupported(): boolean {
    return Howler.usingWebAudio;
  }
}

// Export singleton instance
export const howlerAudio = HowlerAudioManager.getInstance(); 